import { Avatar, Box, Divider, Grid, IconButton, List, ListItem, ListItemAvatar, ListItemText, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLanguageContext } from "../../../contexts/LanguageContext";
import DefaultConstants from "../../../data/Constants";
import Dialog, { IDefaultDialogProps } from "../../Dialog";
import Empty from "../../Empty";
import DateTimeHelper from "../../../helpers/DateTimeHelper";
import { ActionButton } from "../../Button";
import { GovernanceStatusData, IDefaultStatusData, RoomLogTypeEnum, RoomStatusData, RoomStatusEnum } from "../../../data/Enums";
import useDialog from "../../../hooks/useDialog";
import { AttachFile } from "@mui/icons-material";
import ViewAttachmentsDialog from "./ViewAttachmentsDialog";
import IRoomLog from "../../../interfaces/IRoomLog";
import useState from "../../../hooks/useState";
import { IRoom } from "../../../interfaces/IPartnerPms";


interface ILogsDialogProps extends IDefaultDialogProps {
    room: IRoom;
}


interface IStateValues {
    selectedItem: IRoomLog | undefined;
}


const LogsDialog: React.FunctionComponent<ILogsDialogProps> = ({
    open,
    handleClose,
    room
}) => {
    const { translate } = useLanguageContext();
    
    const state = useState<IStateValues>({
        selectedItem: undefined
    });

    const viewAttachmentsDialog = useDialog();


    const contentRef = useRef<HTMLDivElement | null>(null);

    const scrollContentToBottom = useCallback(() => {
        if (contentRef.current === null) {
            return;
        }

        contentRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, [contentRef]);


    useEffect(() => {
        viewAttachmentsDialog.handleClose();

        scrollContentToBottom();
        // eslint-disable-next-line
    }, [open, room.logs]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='close' onClick={handleClose} />
        ];
        // eslint-disable-next-line
    }, [translate, room]);


    const createGovernanceStatusText = useCallback((governanceStatusId: string) => {
        const governanceStatus = Object.values(GovernanceStatusData).find(s => s.partnerId === governanceStatusId) as IDefaultStatusData;
        
        return <Typography className='text-icon' sx={{ backgroundColor: `${governanceStatus.color} !important` }}>
            <governanceStatus.Icon />
            {translate(governanceStatus.name)}
        </Typography>;
        // eslint-disable-next-line
    }, []);
    
    
    const createDoNotDisturbText = useCallback((enabled: boolean) => {
        const doNotDisturb = RoomStatusData[RoomStatusEnum.DoNotDisturb];
    
        let color = doNotDisturb.color;
        let description = 'enabled';
    
        if (!enabled) {
            color = '#bdbdbd';
            description = 'disabled';
        }
    
        return <Typography className='text-icon' sx={{ backgroundColor: `${color} !important` }}>
            <doNotDisturb.Icon />
            {translate(description)}
        </Typography>;
        // eslint-disable-next-line
    }, []);
    
    
    const createExecutionPriorityText = useCallback((enabled: boolean) => {
        const executionPriority = RoomStatusData[RoomStatusEnum.ExecutionPriority];
    
        let color = executionPriority.color;
        let description = 'enabled';
    
        if (!enabled) {
            color = '#bdbdbd';
            description = 'disabled';
        }
    
        return <Typography className='text-icon' sx={{ backgroundColor: `${color} !important` }}>
            <executionPriority.Icon />
            {translate(description)}
        </Typography>;
        // eslint-disable-next-line
    }, []);


    return <>
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate('changeLog')}
            dialogActions={dialogActions}
            className='dialogactions-flexend overflow-inherit roomlogs-dialog'
            absolute={true}
            dividers={true}
            contentRef={contentRef}
        >
            <Grid 
                container 
                columnSpacing={DefaultConstants.gridColumnSpacing} 
                rowSpacing={DefaultConstants.gridRowSpacing}
                paddingY={2}
            >
                {
                    room.logs === undefined || room.logs.length === 0 ? <>
                        <Grid item xs={12}>
                            <Empty text="noItemsFound" />
                        </Grid>
                    </> : <>
                        <Grid item xs={12}>
                            <List>
                                {
                                    room.logs.map((row, index) => {
                                        let typeText = undefined;
                                        let previousValueText = (row.previousValue ?? '-') as any;
                                        let newValueText = (row.newValue ?? '-') as any;
                                                
                                        if (row.type === RoomLogTypeEnum.UpdateGovernanceStatus) {
                                            typeText = '_roomLogChangeGovernanceStatusAction';
                                        
                                            previousValueText = createGovernanceStatusText(row.previousValue);
                                            newValueText = createGovernanceStatusText(row.newValue);
                                        } 
                                        
                                        else if (row.type === RoomLogTypeEnum.UpdateDoNotDisturb) {
                                            typeText = '_roomLogChangeDoNotDisturbStatusAction';
                                            
                                            previousValueText = createDoNotDisturbText(row.previousValue.toLowerCase() === 'true');
                                            newValueText = createDoNotDisturbText(row.newValue.toLowerCase() === 'true');
                                        } 
                                        
                                        else {
                                            typeText = '_roomLogChangeExecutionPriorityAction';
                                            
                                            previousValueText = createExecutionPriorityText(row.previousValue.toLowerCase() === 'true');
                                            newValueText = createExecutionPriorityText(row.newValue.toLowerCase() === 'true');
                                        }

                                        return <>
                                            {
                                                index > 0 && <Box paddingY={2}>
                                                    <Divider />
                                                </Box>
                                            }

                                            <ListItem key={`log-${row.id}-item-1`} disablePadding={true}>
                                                <ListItemAvatar>
                                                    <Avatar
                                                        alt={row.user.fullName.toUpperCase()} 
                                                        src={row.user.avatar} 
                                                        className='fontsize-14'
                                                    />
                                                </ListItemAvatar>

                                                <ListItemText primary={row.user.fullName} secondary={row.user.login} />

                                                <ListItemText
                                                    primary={DateTimeHelper.Format(row.updatedAt, 'DD/MM/YYYY')} 
                                                    secondary={DateTimeHelper.Format(row.updatedAt, 'HH:mm:ss')}
                                                    className='textalign-right'
                                                />
                                            </ListItem>

                                            <ListItem key={`log-${row.id}-item-2`} disablePadding={true}>
                                                <ListItemText 
                                                    primary={translate(typeText)} 
                                                    secondary={<>
                                                        <Box className='display-flex gap-12 alignitems-center'>
                                                            {translate('from').toLowerCase()}
                                                            {' '}
                                                            <strong>{previousValueText}</strong>
                                                            {' '}
                                                            {translate('to').toLowerCase()}
                                                            {' '}
                                                            <strong>{newValueText}</strong>

                                                            {
                                                                row.executionTime && <>
                                                                    ({translate('executedAt').toLowerCase()} {' '} {DateTimeHelper.ToElapsedTime(row.executionTime)})
                                                                </>
                                                            }
                                                        </Box>

                                                        {
                                                            row.observations && row.observations.trim().length > 0 && <Box paddingTop={1.2}> 
                                                                <strong>{translate('observations')}:</strong>
                                                                {' '}
                                                                {row.observations}
                                                            </Box>
                                                        }
                                                    </>}
                                                />

                                                {
                                                    row.attachments !== undefined && row.attachments.length > 0 && (
                                                        <IconButton 
                                                            onClick={() => {
                                                                state.update('selectedItem', row);
                                                                viewAttachmentsDialog.handleOpen();
                                                            }} 
                                                            className='borderradius-default backgroundcolor-grey-2 text-color-1 display-flex gap-8'
                                                        >
                                                            <Typography className='fontsize-14'>{row.attachments.length}</Typography>
                                                            <AttachFile fontSize="small" />
                                                        </IconButton>
                                                    )
                                                }
                                            </ListItem>
                                        </>;
                                    })
                                }
                            </List>
                        </Grid>
                    </>
                }
            </Grid>
        </Dialog>

        <ViewAttachmentsDialog
            open={viewAttachmentsDialog.open}
            handleClose={() => viewAttachmentsDialog.handleClose()}
            attachments={state.values.selectedItem?.attachments ?? []}
        />
    </>;
}

export default LogsDialog;